import React, { useMemo } from "react";
import { Mafs, Coordinates, Polygon, Circle, Text, Theme } from "mafs";

/**
 * CompositeAreaPlanner
 *
 * Java alignment:
 * - CompositeAreaConfig: { subShapes: SubShape[], showLabels: boolean, showGrid: boolean }
 * - SubShape: { type, operation, label, dimensions: [{label,value}], xOffset, yOffset, color }
 *
 * Supported PrimitiveType:
 * - RECTANGLE: dimensions -> width, height
 * - TRIANGLE:  dimensions -> base, height   (rendered as a right triangle from (xOffset, yOffset))
 * - CIRCLE:    dimensions -> radius
 * - SEMICIRCLE:dimensions -> radius         (rendered as a top semicircle with diameter on y=yOffset)
 *
 * Notes:
 * - Dimension lookup is CASE-INSENSITIVE to match Java validator behavior.
 * - Grid uses Coordinates.Cartesian (Mafs.Grid is not a Mafs component).
 */
export const CompositeAreaPlanner = ({ config = {} }) => {
    const { subShapes = [], showLabels = true, showGrid = true } = config;

    const norm = (v) => (v ?? "").toString().trim().toLowerCase();
    const asNum = (v, fallback) => {
        const n = typeof v === "number" ? v : Number(v);
        return Number.isFinite(n) ? n : fallback;
    };

    const getDim = (shape, label, fallback) => {
        const dims = Array.isArray(shape?.dimensions) ? shape.dimensions : [];
        const target = norm(label);
        const found = dims.find((d) => norm(d?.label) === target);
        return asNum(found?.value, fallback);
    };

    const getShapeGeometry = (shape) => {
        const type = (shape?.type ?? "").toString().trim().toUpperCase();
        const x0 = asNum(shape?.xOffset, 0);
        const y0 = asNum(shape?.yOffset, 0);

        if (type === "RECTANGLE") {
            const w = getDim(shape, "width", 4);
            const h = getDim(shape, "height", 2);

            const points = [
                [x0, y0],
                [x0 + w, y0],
                [x0 + w, y0 + h],
                [x0, y0 + h],
            ];

            return {
                kind: "POLYGON",
                points,
                bounds: { minX: x0, maxX: x0 + w, minY: y0, maxY: y0 + h },
                labelAnchor: { x: x0 + w / 2, y: y0 + h / 2, attach: "n" },
            };
        }

        if (type === "TRIANGLE") {
            // Assumption: right triangle with a horizontal base and vertical height
            // with the right-angle at (xOffset, yOffset).
            const base = getDim(shape, "base", 4);
            const height = getDim(shape, "height", 3);

            const points = [
                [x0, y0],
                [x0 + base, y0],
                [x0, y0 + height],
            ];

            const minX = Math.min(x0, x0 + base);
            const maxX = Math.max(x0, x0 + base);
            const minY = Math.min(y0, y0 + height);
            const maxY = Math.max(y0, y0 + height);

            // Centroid
            const cx = (points[0][0] + points[1][0] + points[2][0]) / 3;
            const cy = (points[0][1] + points[1][1] + points[2][1]) / 3;

            return {
                kind: "POLYGON",
                points,
                bounds: { minX, maxX, minY, maxY },
                labelAnchor: { x: cx, y: cy, attach: "n" },
            };
        }

        if (type === "CIRCLE") {
            const r = getDim(shape, "radius", 2);

            return {
                kind: "CIRCLE",
                center: [x0, y0],
                radius: r,
                bounds: { minX: x0 - r, maxX: x0 + r, minY: y0 - r, maxY: y0 + r },
                labelAnchor: { x: x0, y: y0, attach: "n" },
            };
        }

        if (type === "SEMICIRCLE") {
            // Assumption: top semicircle (flat side along diameter y=yOffset).
            // We approximate with a polyline; Polygon closes with the diameter segment.
            const r = getDim(shape, "radius", 2);
            const segments = 40;

            const points = [];
            for (let i = 0; i <= segments; i++) {
                const t = Math.PI - (Math.PI * i) / segments; // π -> 0
                const x = x0 + r * Math.cos(t);
                const y = y0 + r * Math.sin(t);
                points.push([x, y]);
            }

            return {
                kind: "POLYGON",
                points,
                bounds: { minX: x0 - r, maxX: x0 + r, minY: y0, maxY: y0 + r },
                labelAnchor: { x: x0, y: y0 + r / 2, attach: "n" },
            };
        }

        return { kind: "UNKNOWN" };
    };

    const viewBox = useMemo(() => {
        if (!Array.isArray(subShapes) || subShapes.length === 0) {
            return { x: [-5, 5], y: [-5, 5] };
        }

        let minX = Infinity,
            maxX = -Infinity,
            minY = Infinity,
            maxY = -Infinity;

        for (const shape of subShapes) {
            const g = getShapeGeometry(shape);
            if (!g?.bounds) continue;

            minX = Math.min(minX, g.bounds.minX);
            maxX = Math.max(maxX, g.bounds.maxX);
            minY = Math.min(minY, g.bounds.minY);
            maxY = Math.max(maxY, g.bounds.maxY);
        }

        if (![minX, maxX, minY, maxY].every(Number.isFinite)) {
            return { x: [-5, 5], y: [-5, 5] };
        }

        const rangeX = maxX - minX;
        const rangeY = maxY - minY;

        const padX = Math.max(1, rangeX * 0.15);
        const padY = Math.max(1, rangeY * 0.15);

        return {
            x: [minX - padX, maxX + padX],
            y: [minY - padY, maxY + padY],
        };
    }, [subShapes]);

    return (
        <div className="flexColumn gap-small">
            <Mafs viewBox={viewBox} pan={false} zoom={false}>
                {showGrid && <Coordinates.Cartesian />}

                {subShapes.map((shape, idx) => {
                    const op = (shape?.operation ?? "").toString().trim().toUpperCase(); // ADD | SUBTRACT
                    const isSubtract = op === "SUBTRACT";

                    const color = isSubtract ? Theme.red : shape?.color || Theme.blue;
                    const fillOpacity = isSubtract ? 0.08 : 0.25;
                    const strokeStyle = isSubtract ? "dashed" : "solid";

                    const g = getShapeGeometry(shape);
                    if (g.kind === "UNKNOWN") return null;

                    const labelText = typeof shape?.label === "string" ? shape.label.trim() : "";

                    return (
                        <React.Fragment key={idx}>
                            {g.kind === "POLYGON" && (
                                <Polygon
                                    points={g.points}
                                    color={color}
                                    fillOpacity={fillOpacity}
                                    strokeStyle={strokeStyle}
                                />
                            )}

                            {g.kind === "CIRCLE" && (
                                <Circle
                                    center={g.center}
                                    radius={g.radius}
                                    color={color}
                                    fillOpacity={fillOpacity}
                                    strokeStyle={strokeStyle}
                                />
                            )}

                            {showLabels && labelText && g?.labelAnchor && (
                                <Text
                                    x={g.labelAnchor.x}
                                    y={g.labelAnchor.y}
                                    size={14}
                                    attach={g.labelAnchor.attach || "ne"}
                                >
                                    {labelText}
                                </Text>
                            )}
                        </React.Fragment>
                    );
                })}
            </Mafs>
        </div>
    );
};
